import { useQuery } from '@apollo/client';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import Avatar from '@atlaskit/avatar';
import Button from '@atlaskit/button';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import Pagination from '@atlaskit/pagination';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { RuleHistory, RuleHistoryQuery, SortOrder } from '@css-xp/apd-common';
import { ErrorComponents } from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { CustomPagination } from '@css-xp/base/custom-pagination';
import { ruleHistory } from '../../queries/decisions';
import './rule-history.scss';

interface RuleHistoryProps {
  decisionDefinitionKey: string;
}

const handleSelfPaginated = (onPageChange: any, currentPage = 1, totalCount?: number, rowsPerPage?: number) => {
  if (totalCount && rowsPerPage && totalCount <= rowsPerPage) {
    return null;
  }
  const totalPages =
    totalCount && rowsPerPage ? [...Array(Math.ceil(totalCount / rowsPerPage)).keys()].map((num) => num + 1) : [1];
  return (
    <Pagination
      pages={totalPages}
      onChange={(_e, pageNumber) => onPageChange(pageNumber)}
      defaultSelectedIndex={currentPage - 1}
      selectedIndex={currentPage - 1}
      testId="controlled-pagination"
    />
  );
};

const RuleHistoryRef = forwardRef(({ decisionDefinitionKey }: RuleHistoryProps, ref) => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(8);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortingOrder, setSortingOrder] = useState<SortOrder>(SortOrder.DESC);
  const user = useApplicationContext();
  const userId = user?.userId?.split('@')?.[0];

  const { data, loading, error, refetch } = useQuery<RuleHistoryQuery>(ruleHistory, {
    skip: !user?.userId,
    variables: {
      decisionDefinitionKey,
      id: userId,
      pageNumber: currentPage - 1,
      pageSize: rowsPerPage,
      sortBy: 'updatedOn',
      sortOrder: sortingOrder,
    },
  });

  const ruleHistoryData = useMemo(() => {
    return data?.ruleHistory as RuleHistory;
  }, [data]);

  const renderRuleChanges = useCallback(() => {
    return (
      <>
        {(ruleHistoryData?.decisionChangeSummaries ?? []).map((item) => {
          const oldValue = item?.oldValue?.trim();
          const newValue = item?.newValue?.trim();

          return (
            <div
              className="rule-history-row-container"
              key={`rule-history-row-${item?.auditId}`}
              data-testid={`rule-history-row-${item?.auditId}`}
            >
              <div>
                <Avatar src={undefined} size="small" />
              </div>
              <div>
                <span>
                  <b>{item?.fullName}</b> {item?.message}
                  {'  '}
                  <span className="rule-history-time">{item?.updatedDateTime}</span>
                </span>
                {(oldValue || newValue) && (
                  <div className="rule-history-content-row" data-testid={`rule-content-changes-${item?.auditId}`}>
                    <div {...(!oldValue ? { className: 'none' } : '')}>{oldValue ? oldValue : 'None'}</div>
                    <div>
                      <ArrowRightIcon label="changed to" />
                    </div>
                    <div {...(!newValue ? { className: 'none' } : '')}>{newValue ? newValue : 'None'}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </>
    );
  }, [ruleHistoryData]);

  const toggleSorting = useCallback(() => {
    if (sortingOrder === SortOrder.DESC) {
      setSortingOrder(SortOrder.ASC);
    } else {
      setSortingOrder(SortOrder.DESC);
    }
    setCurrentPage(1);
  }, [sortingOrder]);

  const handleRowsChange = useCallback(
    (rowsCount: number) => {
      setRowsPerPage(rowsCount);
      setCurrentPage(1);
    },
    [ruleHistoryData]
  );

  const onPageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
    },
    [ruleHistoryData]
  );

  const totalElements = useMemo(() => {
    return data?.ruleHistory?.totalElements ?? 0;
  }, [ruleHistoryData]);

  useImperativeHandle(ref, () => ({
    reloadRuleHistory: refetch,
  }));

  if (loading) {
    return (
      <div className="rule-history-loading">
        <LoadingIndicator />
      </div>
    );
  }

  if (error) return <ErrorComponents.GenericErrorPage error={error} />;

  if (ruleHistoryData?.decisionChangeSummaries?.length) {
    return (
      <>
        <div className="break-flex" />
        <div className="rule-history-container" data-testid="rule-history-section">
          <div className="rule-history-header">
            <div>
              <h4>History</h4>
            </div>
            <div>
              <Button testId="updatedon-sorting" onClick={toggleSorting}>
                {sortingOrder === SortOrder.ASC ? 'Newest first' : 'Oldest first'}
              </Button>
            </div>
          </div>
          {renderRuleChanges()}
          <div className="rule-history-pagination-wrapper">
            <CustomPagination
              rowCount={rowsPerPage}
              totalCount={totalElements}
              onChangeRow={handleRowsChange}
              currentPage={currentPage}
              selfPaginatedElement={handleSelfPaginated(onPageChange, currentPage, totalElements, rowsPerPage)}
            />
          </div>
        </div>
      </>
    );
  }
  return null;
});

export default RuleHistoryRef;
